// Sugalarm — app-styled confirm / alert dialog context (cycle 210).
//
// Replaces every web-layer `window.confirm` / `window.alert` (the stock
// OS dialog) with the app-chromed `ConfirmModal`, so no native dialog
// ever appears over the app on any surface. The provider owns a single
// active request + its promise resolver; `useConfirm()` exposes async
// `confirm()` / `alert()` so a call site keeps the exact same control
// flow it had with `window.confirm`:
//
//   if (!(await confirm("Disconnect the CGM?"))) return;   // was window.confirm
//
// One request at a time (these are user-driven, serial actions). If a
// second request arrives while one is open, the open one resolves as
// cancelled first — it can never wedge a dangling promise.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ConfirmModal } from "../components/ui/ConfirmModal";

export interface ConfirmOptions {
  message: string;
  /** Uppercase eyebrow (defaults to "Confirm", or "Remove" if destructive). */
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red destructive primary button (delete / remove / revoke / unlink). */
  destructive?: boolean;
}

export interface AlertOptions {
  message: string;
  title?: string;
  dismissLabel?: string;
}

export interface ConfirmValue {
  /** Resolve `true` on confirm, `false` on cancel / tap-outside / Escape. */
  confirm: (opts: string | ConfirmOptions) => Promise<boolean>;
  /** Single-button acknowledge. Resolves when dismissed. */
  alert: (opts: string | AlertOptions) => Promise<void>;
}

interface ActiveRequest {
  message: string;
  title?: string;
  confirmLabel?: string;
  cancelLabel: string | null;
  destructive: boolean;
}

const NOOP: ConfirmValue = {
  // Outside the provider, fall back to never-confirmed / immediate-ack so a
  // stray call can't proceed with a destructive action or hang.
  confirm: () => Promise.resolve(false),
  alert: () => Promise.resolve(),
};

const ConfirmCtx = createContext<ConfirmValue | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveRequest | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  // Settle the open request (if any) and clear it. `result` flows to the
  // confirm promise; the alert promise ignores it (always resolves void).
  const settle = useCallback((result: boolean) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setActive(null);
    resolve?.(result);
  }, []);

  const confirm = useCallback(
    (opts: string | ConfirmOptions) => {
      const o = typeof opts === "string" ? { message: opts } : opts;
      return new Promise<boolean>((resolve) => {
        // A second request supersedes an open one (cancel the old).
        resolveRef.current?.(false);
        resolveRef.current = resolve;
        setActive({
          message: o.message,
          title: o.title,
          confirmLabel: o.confirmLabel ?? (o.destructive ? "Remove" : "OK"),
          cancelLabel: o.cancelLabel ?? "Cancel",
          destructive: o.destructive ?? false,
        });
      });
    },
    [],
  );

  const alert = useCallback((opts: string | AlertOptions) => {
    const o = typeof opts === "string" ? { message: opts } : opts;
    return new Promise<void>((resolve) => {
      resolveRef.current?.(false);
      resolveRef.current = () => resolve();
      setActive({
        message: o.message,
        title: o.title,
        confirmLabel: o.dismissLabel ?? "OK",
        cancelLabel: null,
        destructive: false,
      });
    });
  }, []);

  const value = useMemo<ConfirmValue>(
    () => ({ confirm, alert }),
    [confirm, alert],
  );

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      <ConfirmModal
        open={active != null}
        title={active?.title}
        message={active?.message ?? ""}
        confirmLabel={active?.confirmLabel}
        cancelLabel={active ? active.cancelLabel : "Cancel"}
        destructive={active?.destructive ?? false}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmCtx.Provider>
  );
}

/** Read the confirm/alert API. Returns a safe no-op fallback outside the
 *  provider (confirm → false, alert → resolved) so a call never throws. */
export function useConfirm(): ConfirmValue {
  return useContext(ConfirmCtx) ?? NOOP;
}
